// Fotos anklickbar mit Galerieansicht (15.10.2026, Betreiber): Jede Fotoreihe öffnet beim Antippen eine Vollbildansicht
// mit Wischen bzw. Pfeilen, Bezeichnung (z. B. „Rechte Wand, Teil 2 von 3“), Qualitätshinweis und Löschen. Gelöschte
// Wandfotos fordert die Wandkarte danach gezielt neu an (ui/Waende.tsx).

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { PhotoStep } from "@umzugsscan/shared";
import type { Photo } from "../zustand.ts";
import { Vorschau, qualityIssue, usePhotoUrl } from "./Bild.tsx";

const LABEL: Partial<Record<PhotoStep, string>> = {
  tuer: "Vom Eingang in den Flur",
  flur: "Flur",
  flur_knick: "Um die Ecke / in den Abzweig",
  flur_links: "Linke Wand",
  flur_rechts: "Rechte Wand",
  flur_zurueck: "Zurück zum Eingang",
  rundum: "Rundum-Foto",
  schrank_offen: "Schrank geöffnet",
  wand_gegenueber: "Gegenüberliegende Wand",
  wand_rechts: "Rechte Wand",
  wand_links: "Linke Wand",
  wand_tuer: "Wand mit der Tür",
  wand_nische: "Ecke oder Nische",
  klein_rein: "Aus der Tür in den Raum",
  klein_zurueck: "Zurück zur Tür",
  lampe: "Lampe",
};

/** Bezeichnung je Foto in der Reihe, Teile gleicher Art (und gleichen Flurabschnitts) gezählt. */
export function photoLabels(photos: Photo[]): string[] {
  const key = (p: Photo) => `${p.step}|${p.section ?? 1}`;
  const total = new Map<string, number>();
  for (const p of photos) total.set(key(p), (total.get(key(p)) ?? 0) + 1);
  const seen = new Map<string, number>();
  return photos.map((p) => {
    const k = (seen.get(key(p)) ?? 0) + 1;
    seen.set(key(p), k);
    const n = total.get(key(p))!;
    const base = LABEL[p.step] ?? "Foto";
    return n > 1 ? `${base}, Teil ${k} von ${n}` : base;
  });
}

function Bildschirm(props: { photos: Photo[]; index: number; onIndex: (i: number) => void; onClose: () => void; onDelete?: (p: Photo) => void }) {
  const { photos, index } = props;
  const p = photos[index]!;
  const url = usePhotoUrl(p);
  const labels = photoLabels(photos);
  const issue = qualityIssue(p);
  const start = useRef<number | null>(null);
  const [confirm, setConfirm] = useState(false);
  const go = (d: number) => props.onIndex((index + d + photos.length) % photos.length);

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  });
  useEffect(() => setConfirm(false), [index]);

  return (
    <div
      className="galerie"
      role="dialog"
      aria-modal="true"
      aria-label="Foto ansehen"
      onTouchStart={(e) => (start.current = e.touches[0]?.clientX ?? null)}
      onTouchEnd={(e) => {
        const x = e.changedTouches[0]?.clientX;
        if (start.current !== null && x !== undefined && Math.abs(x - start.current) > 50) go(x < start.current ? 1 : -1);
        start.current = null;
      }}
    >
      <div className="galerie-kopf">
        <span>
          {index + 1} / {photos.length} · {labels[index]}
          {issue && <span className="badge badge-warn">{issue}</span>}
        </span>
        <button type="button" className="galerie-zu" aria-label="Schließen" onClick={props.onClose}>
          ×
        </button>
      </div>
      <div className="galerie-bild">{url ? <img src={url} alt={labels[index]} /> : <span className="thumb-empty" />}</div>
      <div className="galerie-fuss">
        {photos.length > 1 && (
          <button type="button" className="btn btn-quiet" onClick={() => go(-1)} aria-label="Vorheriges Foto">
            ‹
          </button>
        )}
        {props.onDelete &&
          (confirm ? (
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => {
                props.onDelete!(p);
                if (photos.length <= 1) props.onClose();
                else props.onIndex(Math.min(index, photos.length - 2));
              }}
            >
              Wirklich löschen
            </button>
          ) : (
            <button type="button" className="btn btn-secondary" onClick={() => setConfirm(true)}>
              Foto löschen
            </button>
          ))}
        {photos.length > 1 && (
          <button type="button" className="btn btn-quiet" onClick={() => go(1)} aria-label="Nächstes Foto">
            ›
          </button>
        )}
      </div>
    </div>
  );
}

/** Fotoreihe: Vorschaubilder, Antippen öffnet die Galerie; × bzw. „Foto löschen“ löscht. */
export function FotoReihe(props: { photos: Photo[]; onDelete?: (p: Photo) => void; /** nebeneinander in einer Wandreihe (30.10.2026) */ reihe?: boolean }) {
  const [open, setOpen] = useState<number | null>(null);
  if (props.photos.length === 0) return null;
  return (
    <>
      <div className={props.reihe ? "grid reihe" : "grid"}>
        {props.photos.map((p, i) => (
          <Vorschau key={p.id} photo={p} onClick={() => setOpen(i)} onDelete={props.onDelete ? () => props.onDelete!(p) : undefined} />
        ))}
      </div>
      {/* Portal: Karten bilden eigene Stapelebenen, sonst läge die Knopfleiste der Seite über der Galerie. */}
      {open !== null &&
        props.photos[open] &&
        createPortal(<Bildschirm photos={props.photos} index={open} onIndex={setOpen} onClose={() => setOpen(null)} onDelete={props.onDelete} />, document.body)}
    </>
  );
}
